import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { AccountSwitcher, useAuth } from '../auth';
import { AppShell } from './AppShell';
import { Breadcrumb } from './Breadcrumb';
import { RouteErrorBoundary } from './ErrorBoundary';
import { PageResetProvider } from './page-reset';
import { AppConfigProvider } from '../app-config';
import { GalleryTabsBar } from '../gallery/GalleryTabsBar';
import { LIBRARY_ID } from '../gallery/config';
import { useCounts } from '../gallery/use-facets';
import { requestGalleryRefresh } from '../gallery/gallery-refresh';
import { NotificationBell } from '../notifications';

/**
 * `GalleryView` (qui écoute le signal de rafraîchissement) est monté EXACTEMENT sous ces
 * routes — cf. l'arbre de routes dans `App` : `/`, `/dossiers`, `/tags` et `/photo/:id`.
 * Ailleurs (Paramètres, Admin, Outils…) la galerie n'est pas montée pour recevoir le signal.
 */
function isGalleryRoute(pathname: string): boolean {
  return (
    pathname === '/' ||
    pathname === '/dossiers' ||
    pathname === '/tags' ||
    pathname.startsWith('/photo/')
  );
}

/**
 * Coquille AUTHENTIFIÉE partagée — en-tête PERSISTANT sur TOUS les écrans.
 *
 * Route de LAYOUT (au-dessus de la galerie, des réglages, de l'admin et de l'Outil
 * EXIF) : elle rend `AppShell` (donc le fil d'Ariane à gauche + la rangée d'icônes,
 * la cloche et le menu de compte à droite) une seule fois, et délègue le contenu de
 * `main` à ses routes filles via `<Outlet>`. Ainsi le fil d'Ariane reste visible et
 * pilote la navigation, même sur Paramètres / Admin / Outil EXIF (qui étaient
 * naguère des surcouches plein écran masquant l'en-tête).
 *
 * Le compteur du raccourci « À valider (IA) » (badge) est calculé ici (`useCounts`)
 * pour rester correct quel que soit l'écran affiché — c'est un indicateur informatif
 * qui se met à jour au montage / changement de bibliothèque.
 */
export function AppChrome(): React.JSX.Element {
  const { user } = useAuth();
  const libraryId = user?.libraryId ?? LIBRARY_ID;
  const navigate = useNavigate();
  const location = useLocation();

  const counts = useCounts(libraryId);
  const aiReviewCount = counts.data?.aiReview ?? 0;

  const headerActions = (
    <div className="flex items-center gap-1">
      <GalleryTabsBar aiReviewCount={aiReviewCount} />
      {/* Pont cloche → révision des tags IA : ouvre la galerie filtrée « À valider (IA) »
          (`taguees=avalider` = propositions IA en attente, pas encore validées), d'où
          « Tout sélectionner » → « Réviser / valider les tags ». */}
      <NotificationBell
        libraryId={libraryId}
        onReviewAiTags={() => navigate('/?taguees=avalider')}
        onRefreshGallery={() => {
          // Galerie montée → rechargement in-place (filtres préservés). Sinon (Paramètres /
          // Admin / Outils…) elle n'est pas là pour recevoir le signal → aller à l'accueil,
          // qui chargera des données fraîches au montage.
          requestGalleryRefresh();
          if (!isGalleryRoute(location.pathname)) navigate('/');
        }}
      />
      <AccountSwitcher />
    </div>
  );

  return (
    // `AppConfigProvider` : coupe-circuit IA global (Incrément B) exposé à toute la
    // zone authentifiée (masque les entrées IA selon `aiEnabled`).
    // `PageResetProvider` enveloppe le fil d'Ariane (déclencheur) ET l'`<Outlet>` (les
    // pages qui enregistrent leur reset local) → un clic sur le maillon courant
    // réinitialise même une page dont l'état ne vit pas dans l'URL (cf. `page-reset`).
    <AppConfigProvider>
      <PageResetProvider>
        <AppShell nav={<Breadcrumb />} headerActions={headerActions}>
          {/* Frontière de ROUTE : un plantage de rendu d'une page (galerie, dédup,
              visionneuse…) affiche un repli diagnosticable DANS `main` — l'en-tête
              persistant reste utilisable pour naviguer ailleurs — au lieu d'une page
              blanche. Elle se réinitialise à chaque navigation (cf. `RouteErrorBoundary`). */}
          <RouteErrorBoundary>
            <Outlet />
          </RouteErrorBoundary>
        </AppShell>
      </PageResetProvider>
    </AppConfigProvider>
  );
}
